import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import CreateTest from "@/pages/CreateTest";
import EditTest from "@/pages/EditTest";
import ExecutionDetails from "@/pages/ExecutionDetails";
import TestHistory from "@/pages/TestHistory";
import GenerateFromStory from "@/pages/GenerateFromStory";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Settings from "@/pages/Settings";
import AdminDashboard from "@/pages/AdminDashboard";
import TestSuites from "@/pages/TestSuites";
import Schedules from "@/pages/Schedules";
import Analytics from "@/pages/Analytics";
import Teams from "@/pages/Teams";
import SharedDashboard from "@/pages/SharedDashboard";
import InviteAccept from "@/pages/InviteAccept";
import CreateAPITest from "@/pages/CreateAPITest";
import EditAPITest from "@/pages/EditAPITest";
import APIExecutionDetails from "@/pages/APIExecutionDetails";
import ImportCollection from "@/pages/ImportCollection";
import BillingSuccess from "@/pages/BillingSuccess";
import PlanGate from "@/components/PlanGate";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/context/ThemeContext";

// Router component that handles session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions - session_id must be processed BEFORE any auth check
  // Check both location.hash and window.location.hash for full compatibility
  const hashToCheck = location.hash || window.location.hash;

  // If session_id or session_token is in the URL hash, render AuthCallback regardless of path
  if (hashToCheck?.includes('session_id=') || hashToCheck?.includes('session_token=')) {
    console.log('AuthCallback triggered, hash:', hashToCheck);
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/create-test" element={<CreateTest />} />
      <Route path="/edit-test/:testCaseId" element={<EditTest />} />
      <Route path="/execution/:executionId" element={<ExecutionDetails />} />
      <Route path="/history" element={<TestHistory />} />
      <Route path="/generate-from-story" element={<GenerateFromStory />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/suites" element={<TestSuites />} />
      <Route path="/schedules" element={<Schedules />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/teams" element={<Teams />} />
      <Route path="/teams/:teamId/shared" element={<SharedDashboard />} />
      <Route path="/invite/:inviteId" element={<InviteAccept />} />
      <Route path="/create-api-test" element={<CreateAPITest />} />
      <Route path="/edit-api-test/:id" element={<EditAPITest />} />
      <Route path="/api-execution/:executionId" element={<APIExecutionDetails />} />
      <Route path="/import-collection" element={<ImportCollection />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <ErrorBoundary>
          <BrowserRouter>
            <AuthProvider>
              <PlanGate>
                <AppRouter />
              </PlanGate>
            </AuthProvider>
          </BrowserRouter>
        </ErrorBoundary>
        <Toaster position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;
